import { Node, mergeAttributes } from '@tiptap/core';
import type { Command } from '@tiptap/core';

// Declare module augmentation for custom commands
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    emphasisBlock: {
      setEmphasisBlock: () => ReturnType;
      toggleEmphasisBlock: () => ReturnType;
    };
    calloutBox: {
      setCalloutBox: () => ReturnType;
      toggleCalloutBox: () => ReturnType;
    };
    button: {
      setButton: (options: { href: string; text: string; target?: string }) => ReturnType;
    };
  }
}

// Emphasis Block Extension (purple left border highlight)
export const EmphasisBlock = Node.create({
  name: 'emphasisBlock',

  group: 'block',

  content: 'block+',

  defining: true,

  addAttributes() {
    return {};
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="emphasis-block"]',
      },
      {
        tag: 'div.emphasis',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'emphasis-block',
        class: 'emphasis',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setEmphasisBlock:
        (): Command =>
        ({ commands }: any) => {
          return commands.wrapIn(this.name);
        },
      toggleEmphasisBlock:
        (): Command =>
        ({ commands }: any) => {
          return commands.toggleWrap(this.name);
        },
    };
  },
});

// Callout Box Extension (purple background info box)
export const CalloutBox = Node.create({
  name: 'calloutBox',

  group: 'block',

  content: 'block+',

  defining: true,

  addAttributes() {
    return {};
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="callout-box"]',
      },
      {
        tag: 'div.callout',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'callout-box',
        class: 'callout',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setCalloutBox:
        (): Command =>
        ({ commands }: any) => {
          return commands.wrapIn(this.name);
        },
      toggleCalloutBox:
        (): Command =>
        ({ commands }: any) => {
          return commands.toggleWrap(this.name);
        },
    };
  },
});

// Button Extension (styled link button)
export const ButtonNode = Node.create({
  name: 'button',

  group: 'inline',

  inline: true,

  atom: true,

  addAttributes() {
    return {
      href: {
        default: null,
      },
      text: {
        default: 'Button',
      },
      target: {
        default: '_self',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'a[data-type="button"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'button',
        class: 'article-button',
        href: HTMLAttributes.href,
        target: HTMLAttributes.target,
      }),
      HTMLAttributes.text,
    ];
  },

  addCommands() {
    return {
      setButton:
        (options: { href: string; text: string; target?: string }): Command =>
        ({ commands }: any) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              href: options.href,
              text: options.text,
              target: options.target || '_self',
            },
          });
        },
    };
  },
});
